import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Check, ArrowRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { RoomType, StagingStyle, STYLE_LABELS } from '../types';
import { BatchUploadZone, UploadedPhoto } from './BatchUploadZone';
import { api } from '../services/api';

const ROOM_LABELS: Record<string, string> = {
  living_room: 'Woonkamer', bedroom: 'Slaapkamer', kitchen: 'Keuken', bathroom: 'Badkamer',
  office: 'Kantoor', hallway: 'Hal', garden: 'Tuin', exterior: 'Exterieur', other: 'Overig',
};
const ROOM_TYPE_MAP: Record<string, RoomType> = {
  living_room: RoomType.LIVING_ROOM, bedroom: RoomType.BEDROOM,
  kitchen: RoomType.KITCHEN, bathroom: RoomType.BATHROOM,
  office: RoomType.OFFICE,
};
const STAGING_ROOMS = Object.keys(ROOM_TYPE_MAP);

interface ClassifiedPhoto extends UploadedPhoto {
  room: string;
  confidence: number;
  qualityScore: number;
  reason: string;
  selected: boolean;
}

interface Props {
  onStartRestyling: (photos: { file: File; roomType: RoomType }[], style: StagingStyle) => void;
  /** Open the OS file picker automatically on mount so the user lands in the file dialog without an extra click. */
  autoOpenPicker?: boolean;
  /**
   * STA-200: when set, classification still runs (for quality scoring +
   * best-of selection), but the room field is forced to this value and only
   * that room group is shown. Used when entering the batch flow from a
   * specific empty-room tile.
   */
  prescopedRoom?: RoomType;
}

// Reverse-map RoomType enum value → AI classifier's lowercase key.
const ROOM_TYPE_TO_KEY: Record<string, string> = {
  [RoomType.LIVING_ROOM]: 'living_room',
  [RoomType.BEDROOM]: 'bedroom',
  [RoomType.KITCHEN]: 'kitchen',
  [RoomType.BATHROOM]: 'bathroom',
  [RoomType.OFFICE]: 'office',
};

export const SmartUpload: React.FC<Props> = ({ onStartRestyling, autoOpenPicker = false, prescopedRoom }) => {
  const [style, setStyle] = useState<StagingStyle>(StagingStyle.MODERN);
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [classified, setClassified] = useState<ClassifiedPhoto[]>([]);
  const [classifying, setClassifying] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const autoClassifyTriggeredRef = useRef(false);
  const pickerOpenedRef = useRef(false);

  const classifyAll = async () => {
    if (photos.length === 0) return;
    setClassifying(true);
    setProgress({ done: 0, total: photos.length });

    const results: ClassifiedPhoto[] = [];

    // Process in batches of 5 for parallel speed
    for (let i = 0; i < photos.length; i += 5) {
      const batch = photos.slice(i, i + 5);
      const promises = batch.map(async (photo) => {
        try {
          const reader = new FileReader();
          const dataUrl: string = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Foto kon niet worden gelezen'));
            reader.onabort = () => reject(new Error('Foto lezen afgebroken'));
            reader.readAsDataURL(photo.file);
          });

          const data = await api.post<{ room: string; confidence: number; qualityScore: number; reason: string }>('/api/ai/classify-photo', { image: dataUrl });

          if (!data || typeof data.room !== 'string' || !Number.isFinite(data.confidence) || !Number.isFinite(data.qualityScore)) {
            throw new Error('Ongeldige classificatie');
          }

          return {
            ...photo,
            room: data.room,
            confidence: data.confidence,
            qualityScore: data.qualityScore,
            reason: data.reason,
            selected: false,
          } as ClassifiedPhoto;
        } catch {
          return {
            ...photo,
            room: 'other',
            confidence: 0,
            qualityScore: 0,
            reason: 'Classificatie mislukt',
            selected: false,
          } as ClassifiedPhoto;
        }
      });

      const batchResults = await Promise.all(promises);
      results.push(...batchResults);
      setProgress({ done: results.length, total: photos.length });
    }

    // STA-200: als prescopedRoom gezet is, override de room voor alle foto's.
    // Kwaliteits-score blijft uit de classifier komen — die wordt nog steeds
    // gebruikt voor de best-of selectie binnen de prescoped ruimte.
    const prescopedKey = prescopedRoom ? ROOM_TYPE_TO_KEY[prescopedRoom] : null;
    const adjusted = prescopedKey
      ? results.map(p => ({ ...p, room: prescopedKey }))
      : results;

    // Auto-select beste foto per ruimte
    const bestPerRoom: Record<string, ClassifiedPhoto> = {};
    for (const photo of adjusted) {
      if (!STAGING_ROOMS.includes(photo.room)) continue;
      if (!bestPerRoom[photo.room] || photo.qualityScore > bestPerRoom[photo.room].qualityScore) {
        bestPerRoom[photo.room] = photo;
      }
    }
    const withSelection = adjusted.map(p => ({
      ...p,
      selected: bestPerRoom[p.room]?.id === p.id,
    }));

    setClassified(withSelection);
    setClassifying(false);
  };

  const toggleSelection = (id: string, room: string) => {
    setClassified(prev => prev.map(p => {
      if (p.room === room) return { ...p, selected: p.id === id };
      return p;
    }));
  };

  const assignRoom = (id: string, room: string) => {
    setClassified(prev => {
      const moved = prev.find(p => p.id === id);
      if (!moved || !STAGING_ROOMS.includes(room)) return prev;
      const next = prev.map(p => p.id === id ? { ...p, room, selected: true }
        : p.room === room ? { ...p, selected: false } : p);
      // Keep one best-of choice in the original room when its selected photo moves.
      if (moved.selected && moved.room !== room) {
        const replacement = next.filter(p => p.room === moved.room)
          .sort((a, b) => b.qualityScore - a.qualityScore)[0];
        if (replacement) return next.map(p => p.id === replacement.id ? { ...p, selected: true } : p);
      }
      return next;
    });
  };

  const roomPicker = (photo: ClassifiedPhoto) => (
    <select
      aria-label={`Ruimte voor ${photo.name}`}
      value={STAGING_ROOMS.includes(photo.room) ? photo.room : ''}
      onChange={event => assignRoom(photo.id, event.target.value)}
      className="w-full text-xs border border-border rounded bg-white p-1"
    >
      <option value="" disabled>Kies een ruimte</option>
      {STAGING_ROOMS.map(room => <option key={room} value={room}>{ROOM_LABELS[room]}</option>)}
    </select>
  );

  const selectedPhotos = classified.filter(p => p.selected);
  const prescopedKey = prescopedRoom ? ROOM_TYPE_TO_KEY[prescopedRoom] : null;
  const visibleRooms = prescopedKey ? [prescopedKey] : STAGING_ROOMS;
  const roomGroups = visibleRooms.map(room => ({
    room,
    label: ROOM_LABELS[room],
    photos: classified.filter(p => p.room === room),
    selected: classified.find(p => p.room === room && p.selected),
  }));
  // STA-200: bij prescoped flow tonen we de "overige" stapel niet — alle
  // foto's worden geforceerd naar de prescoped ruimte.
  const otherPhotos = prescopedKey ? [] : classified.filter(p => !STAGING_ROOMS.includes(p.room));

  const [starting, setStarting] = useState(false);

  // Open de OS file picker direct als de panel met autoOpenPicker is gemount.
  useEffect(() => {
    if (!autoOpenPicker) return;
    if (pickerOpenedRef.current) return;
    if (photos.length > 0 || classified.length > 0) return;
    pickerOpenedRef.current = true;
    // Wacht tot de input in de DOM staat.
    const t = window.setTimeout(() => {
      const input = document.getElementById('batchFileInput') as HTMLInputElement | null;
      input?.click();
    }, 0);
    return () => window.clearTimeout(t);
  }, [autoOpenPicker, photos.length, classified.length]);

  // Start classificatie automatisch zodra de eerste foto's binnen zijn.
  // Zo hoeft de gebruiker geen losse "AI analyseert"-knop te indrukken.
  useEffect(() => {
    if (autoClassifyTriggeredRef.current) return;
    if (photos.length === 0) return;
    if (classifying) return;
    if (classified.length > 0) return;
    autoClassifyTriggeredRef.current = true;
    void classifyAll();
  }, [photos.length, classifying, classified.length]);

  const handleStartRestyling = () => {
    const toRestyle = selectedPhotos
      .filter(p => ROOM_TYPE_MAP[p.room])
      .map(p => ({ file: p.file, roomType: ROOM_TYPE_MAP[p.room] }));
    if (toRestyle.length === 0) return;
    setStarting(true);
    toast.loading(`${toRestyle.length} foto${toRestyle.length !== 1 ? "'s" : ''} worden gestyled…`, { id: 'smart-upload-start' });
    try {
      onStartRestyling(toRestyle, style);
    } finally {
      setTimeout(() => {
        toast.dismiss('smart-upload-start');
        setStarting(false);
      }, 800);
    }
  };

  // Stap 1: Upload — geen extra knop; classificatie start automatisch via de useEffect hierboven.
  if (classified.length === 0) {
    return (
      <div>
        <BatchUploadZone photos={photos} onPhotosChange={setPhotos} />
        {photos.length > 0 && (
          <div className="mt-4 flex items-center gap-3 text-sm text-muted" aria-live="polite">
            <Loader2 size={14} className="animate-spin text-ink" />
            <span>
              {classifying
                ? `AI analyseert ${progress.done}/${progress.total} foto's…`
                : `Klaarzetten van ${photos.length} foto${photos.length !== 1 ? "'s" : ''}…`}
            </span>
          </div>
        )}
      </div>
    );
  }

  // Stap 2: Overzicht per ruimte
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold">
            {prescopedKey ? `Foto's voor ${ROOM_LABELS[prescopedKey]}` : 'AI-selectie'}
          </h3>
          <p className="text-xs text-muted">
            {prescopedKey
              ? `AI kiest de beste foto · ${selectedPhotos.length} geselecteerd`
              : `${selectedPhotos.length} foto's geselecteerd voor restyling`}
          </p>
        </div>
        <button
          onClick={() => { autoClassifyTriggeredRef.current = false; setClassified([]); setPhotos([]); }}
          className="text-xs text-muted hover:text-ink"
        >
          Opnieuw uploaden
        </button>
      </div>

      <div className="space-y-4 mb-6">
        {roomGroups.map(group => (
          <div key={group.room} className="bg-stone rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              {group.selected ? (
                <div className="w-5 h-5 bg-accent rounded flex items-center justify-center"><Check size={12} strokeWidth={3} /></div>
              ) : (
                <div className="w-5 h-5 border border-border rounded" />
              )}
              <span className="text-sm font-semibold">{group.label}</span>
              <span className="text-xs text-muted">{group.photos.length} foto{group.photos.length !== 1 ? '\'s' : ''}</span>
            </div>
            {group.photos.length === 0 ? (
              <p className="text-xs text-muted pl-7">Geen foto's herkend voor deze ruimte</p>
            ) : (
              <div className="flex gap-2 pl-7 overflow-x-auto">
                {group.photos.sort((a, b) => b.qualityScore - a.qualityScore).map(photo => (
                  <div key={photo.id} className="shrink-0 w-24 space-y-1">
                  <button
                    onClick={() => toggleSelection(photo.id, photo.room)}
                    className={`shrink-0 w-24 rounded-md overflow-hidden border-2 transition-all ${
                      photo.selected ? 'border-accent shadow-card-hover' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <div className="aspect-[4/3] relative">
                      <img src={photo.previewUrl} alt={`${ROOM_LABELS[photo.room] || photo.room} foto`} className="w-full h-full object-cover" />
                      <div className="absolute top-1 right-1 bg-black/60 text-white text-[9px] px-1 rounded">
                        {photo.qualityScore}/10
                      </div>
                      {photo.confidence < 0.7 && (
                        <div className="absolute top-1 left-1">
                          <AlertTriangle size={10} className="text-yellow-400" />
                        </div>
                      )}
                    </div>
                    {photo.selected && (
                      <div className="bg-accent text-center py-0.5 text-[9px] font-semibold">Geselecteerd</div>
                    )}
                  </button>
                  {!prescopedKey && roomPicker(photo)}
                  {photo.reason === 'Classificatie mislukt' && <p className="text-xs text-muted">Classificatie mislukt · ruimte handmatig ingesteld</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {otherPhotos.length > 0 && (
          <div className="border border-border rounded-lg p-4">
            <p className="text-xs text-muted mb-2">Ruimte niet herkend of niet ondersteund · kies handmatig een ruimte</p>
            <div className="flex gap-2 overflow-x-auto">
              {otherPhotos.map(photo => (
                <div key={photo.id} className="shrink-0 w-32 space-y-1">
                  <img src={photo.previewUrl} alt={`${ROOM_LABELS[photo.room] || photo.room} foto`} className="w-full aspect-square rounded object-cover" />
                  <p className="text-xs text-muted">{photo.reason === 'Classificatie mislukt' ? 'Classificatie mislukt · kies handmatig' : ROOM_LABELS[photo.room] || 'Onbekende ruimte'}</p>
                  {roomPicker(photo)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {selectedPhotos.length > 0 && (
        <div className="mb-4">
          <label htmlFor="smart-upload-style" className="block text-sm font-medium mb-2">Designstijl</label>
          <select id="smart-upload-style" value={style} onChange={e => setStyle(e.target.value as StagingStyle)} aria-describedby="smart-upload-style-help" disabled={starting} className="w-full border border-border rounded-md bg-surface p-3 text-sm">
            {Object.values(StagingStyle).map(value => <option key={value} value={value}>{STYLE_LABELS[value]}</option>)}
          </select>
          <p id="smart-upload-style-help" className="text-xs text-muted mt-2">Deze stijl wordt toegepast op alle geselecteerde foto's.</p>
        </div>
      )}

      {selectedPhotos.length > 0 && (
        <button
          onClick={handleStartRestyling}
          disabled={starting}
          className="w-full bg-ink text-white py-3.5 rounded-md text-sm font-medium hover:opacity-85 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {starting ? (
            <><Loader2 size={16} className="animate-spin" /> Starten…</>
          ) : (
            <>Restyle {selectedPhotos.length} foto{selectedPhotos.length !== 1 ? '\'s' : ''} <ArrowRight size={16} /></>
          )}
        </button>
      )}
    </div>
  );
};
